import * as THREE from 'three';

// ─── PlayerMotion ─────────────────────────────────────────────────────────────
// Turns a player's keyframes into a smooth, time-accurate trajectory.
//
// Each segment is a cubic Hermite curve parameterised by *time*, so every
// keyframe is still reached exactly at its `t` (the timeline and the 2D editor
// stay in sync) but the player curves through corners and eases in/out of
// stops instead of snapping. Tangents are zeroed at stops/reversals so the
// spline never overshoots a waiting player.
//
// A per-frame table integrates arc length and the skating stride phase, so the
// stride cadence follows real ground speed and is fully deterministic when the
// timeline is scrubbed.

const SAMPLE_DT = 1 / 120;
const STILL_EPS = 0.04;

// Ground covered by one full stride cycle (left + right push) at a given speed.
// Faster skaters take longer *and* quicker strides; accelerating skaters take
// short, choppy steps.
export function strideLength(speed, accelLong) {
    let L = 1.35 + 0.34 * speed;
    if (accelLong > 0) L *= 1 - Math.min(0.38, accelLong * 0.045);
    return Math.min(4.2, Math.max(1.1, L));
}

export default class PlayerMotion {
    constructor(keyframes) {
        const sorted = [...(keyframes || [])].sort((a, b) => a.t - b.t);
        // Drop keyframes with duplicate times (keep the last one written)
        const kfs = [];
        sorted.forEach(k => {
            if (kfs.length && Math.abs(k.t - kfs[kfs.length - 1].t) < 1e-4) kfs[kfs.length - 1] = k;
            else kfs.push(k);
        });
        if (kfs.length === 0) kfs.push({ t: 0, x: 0, y: 0 });

        this.kfs = kfs;
        this.t0  = kfs[0].t;
        this.t1  = kfs[kfs.length - 1].t;
        this.isStatic = kfs.every(k => Math.hypot(k.x - kfs[0].x, k.y - kfs[0].y) < STILL_EPS);

        this._computeTangents();
        this._buildTable();
    }

    _computeTangents() {
        const k = this.kfs;
        const n = k.length;
        this.mx = new Float64Array(n);
        this.my = new Float64Array(n);
        for (let i = 1; i < n - 1; i++) {
            const dt0 = k[i].t - k[i - 1].t;
            const dt1 = k[i + 1].t - k[i].t;
            const d0x = k[i].x - k[i - 1].x, d0y = k[i].y - k[i - 1].y;
            const d1x = k[i + 1].x - k[i].x, d1y = k[i + 1].y - k[i].y;
            const l0 = Math.hypot(d0x, d0y), l1 = Math.hypot(d1x, d1y);
            if (l0 < STILL_EPS || l1 < STILL_EPS) continue;   // stop → zero velocity

            const v0x = d0x / dt0, v0y = d0y / dt0;
            const v1x = d1x / dt1, v1y = d1y / dt1;
            let mx = (v0x * dt1 + v1x * dt0) / (dt0 + dt1);
            let my = (v0y * dt1 + v1y * dt0) / (dt0 + dt1);

            // Sharper direction change → player slows more through the corner
            const cos = (d0x * d1x + d0y * d1y) / (l0 * l1);
            const turn = Math.max(0, (1 + cos) / 2);
            mx *= turn; my *= turn;

            // Never exceed the slower neighbouring segment by much (prevents overshoot)
            const cap = 1.4 * Math.min(l0 / dt0, l1 / dt1);
            const m = Math.hypot(mx, my);
            if (m > cap) { mx *= cap / m; my *= cap / m; }
            this.mx[i] = mx; this.my[i] = my;
        }
    }

    _segment(t) {
        const k = this.kfs;
        let lo = 0, hi = k.length - 1;
        while (hi - lo > 1) {
            const mid = (lo + hi) >> 1;
            if (k[mid].t <= t) lo = mid; else hi = mid;
        }
        return lo;
    }

    // Analytic position / velocity / acceleration at time t
    sample(t, out = {}) {
        const k = this.kfs;
        if (k.length === 1 || t <= this.t0 || t >= this.t1) {
            const p = t >= this.t1 ? k[k.length - 1] : k[0];
            out.x = p.x; out.y = p.y; out.vx = 0; out.vy = 0; out.ax = 0; out.ay = 0;
            return out;
        }
        const i = this._segment(t);
        const a = k[i], b = k[i + 1];
        const h = b.t - a.t;
        const u = (t - a.t) / h;
        const u2 = u * u, u3 = u2 * u;

        const h00 = 2 * u3 - 3 * u2 + 1, h10 = u3 - 2 * u2 + u, h01 = -2 * u3 + 3 * u2, h11 = u3 - u2;
        const d00 = 6 * u2 - 6 * u,      d10 = 3 * u2 - 4 * u + 1, d01 = -6 * u2 + 6 * u, d11 = 3 * u2 - 2 * u;
        const e00 = 12 * u - 6,          e10 = 6 * u - 4,          e01 = -12 * u + 6,     e11 = 6 * u - 2;

        const m0x = this.mx[i] * h, m0y = this.my[i] * h;
        const m1x = this.mx[i + 1] * h, m1y = this.my[i + 1] * h;

        out.x  = h00 * a.x + h10 * m0x + h01 * b.x + h11 * m1x;
        out.y  = h00 * a.y + h10 * m0y + h01 * b.y + h11 * m1y;
        out.vx = (d00 * a.x + d10 * m0x + d01 * b.x + d11 * m1x) / h;
        out.vy = (d00 * a.y + d10 * m0y + d01 * b.y + d11 * m1y) / h;
        out.ax = (e00 * a.x + e10 * m0x + e01 * b.x + e11 * m1x) / (h * h);
        out.ay = (e00 * a.y + e10 * m0y + e01 * b.y + e11 * m1y) / (h * h);
        return out;
    }

    position(t) {
        const s = this.sample(t, this._tmp || (this._tmp = {}));
        return { x: s.x, y: s.y };
    }

    _buildTable() {
        const span = Math.max(0, this.t1 - this.t0);
        const n = Math.max(2, Math.ceil(span / SAMPLE_DT) + 1);
        this._n = n;
        this._dt = span / (n - 1) || SAMPLE_DT;
        this._arc   = new Float32Array(n);
        this._phase = new Float32Array(n);
        const s = {};
        let arc = 0, phase = 0;
        for (let i = 1; i < n; i++) {
            const t = this.t0 + (i - 0.5) * this._dt;     // midpoint rule
            this.sample(t, s);
            const v = Math.hypot(s.vx, s.vy);
            const aL = v > 1e-3 ? (s.vx * s.ax + s.vy * s.ay) / v : 0;
            arc   += v * this._dt;
            phase += (v / strideLength(v, aL)) * this._dt;
            this._arc[i] = arc;
            this._phase[i] = phase;
        }
        this.totalArc = arc;
    }

    _lookup(table, t) {
        if (t <= this.t0) return table[0];
        if (t >= this.t1) return table[this._n - 1];
        const f = (t - this.t0) / this._dt;
        const i = Math.min(this._n - 2, Math.floor(f));
        const a = f - i;
        return table[i] * (1 - a) + table[i + 1] * a;
    }

    arcAt(t)    { return this._lookup(this._arc, t); }
    phaseAt(t)  { return this._lookup(this._phase, t); }
}

// THREE.Curve over a motion's time span, so drawn paths match the motion exactly
// (curve parameter u is proportional to time).
export class MotionCurve extends THREE.Curve {
    constructor(motion, z = 0.02) {
        super();
        this.motion = motion;
        this.z = z;
    }
    getPoint(u, target = new THREE.Vector3()) {
        const m = this.motion;
        const p = m.position(m.t0 + (m.t1 - m.t0) * u);
        return target.set(p.x, p.y, this.z);
    }
}
